import React from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useLanguage } from "../../i18n/LanguageContext";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  MessageCircle,
  FileText,
  Send,
  Loader2,
  ImagePlus,
  X,
  Paperclip,
} from "lucide-react";

const FollowupChat = ({
  followups,
  followupInput,
  setFollowupInput,
  followupLoading,
  onSubmit,
  followupEndRef,
  followupsLimit = 3,
  followupsRemaining = null,
  followupAttachments = [],
  onFollowupFileUpload,
  onRemoveFollowupAttachment,
  formatFileSize,
  followupFileInputRef,
  followupAcceptString,
  followupFileIcons = {},
  followupFilesRemaining = null,
}) => {
  const { t } = useLanguage();
  const used = followups.length;
  const limit =
    typeof followupsLimit === "number" && followupsLimit > 0
      ? followupsLimit
      : 3;
  const remaining =
    typeof followupsRemaining === "number"
      ? followupsRemaining
      : Math.max(0, limit - used);
  const atLimit = remaining <= 0;
  const canAttach =
    typeof followupFilesRemaining !== "number" || followupFilesRemaining > 0;
  const canSend =
    Boolean(followupInput.trim()) || followupAttachments.length > 0;

  return (
    <Card className="border border-border" data-testid="followup-section">
      <CardHeader className="pb-3 px-4 md:px-6">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-muted-foreground" />
          <CardTitle className="text-sm md:text-base">
            {t('query.followupTitle')}
          </CardTitle>
          <Badge
            variant="secondary"
            className="text-xs"
            data-testid="followup-quota-badge"
          >
            {used}/{limit}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 md:px-6 pt-0">
        {followups.length > 0 && (
          <div className="space-y-4 mb-4">
            {followups.map((fu, idx) => (
              <div key={fu.id || idx} className="space-y-2">
                <div className="flex justify-end">
                  <div className="bg-primary/10 border border-primary/20 text-foreground rounded-lg rounded-br-sm px-3 py-2 max-w-[85%] md:max-w-[75%]">
                    {fu.question ? (
                      <p className="text-sm">{fu.question}</p>
                    ) : null}
                    {Array.isArray(fu.files) && fu.files.length > 0 && (
                      <div className={fu.question ? "mt-2 pt-2 border-t border-primary/15" : ""}>
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          {t("query.followupAttachments")}
                        </p>
                        <ul className="space-y-0.5">
                          {fu.files.map((name) => (
                            <li key={name} className="text-xs flex items-center gap-1">
                              <Paperclip className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-secondary rounded-lg rounded-bl-sm px-3 py-2 max-w-[85%] md:max-w-[75%]">
                    <div className="flex items-center gap-1.5 mb-1">
                      <FileText className="w-3 h-3 text-primary" />
                      <span className="text-xs font-medium text-muted-foreground">{t('query.followupAnswer')}</span>
                    </div>
                    <div className="prose prose-sm max-w-none clinical-prose-results prose-headings:text-foreground prose-p:text-foreground/90 prose-strong:text-foreground prose-li:text-foreground/90 prose-p:my-1 prose-li:my-0">
                      <ReactMarkdown>{fu.answer}</ReactMarkdown>
                    </div>
                    {fu.cost_usd && (
                      <p className="text-xs text-muted-foreground mt-1.5 text-right">
                        ${fu.cost_usd?.toFixed(4)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={followupEndRef} />
          </div>
        )}

        {atLimit ? (
          <div
            className="rounded-md border border-border bg-muted/40 px-3 py-3 space-y-2"
            data-testid="followup-limit-reached"
          >
            <p className="text-sm text-muted-foreground">
              {t("query.followupLimitReached")}
            </p>
            <Button asChild size="sm" variant="outline" data-testid="followup-new-case-cta">
              <Link to="/case/new">{t("query.followupStartNewCase")}</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {followupAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {followupAttachments.map((att) => (
                  <div
                    key={att.id}
                    className="relative group flex items-center gap-2 bg-muted/50 rounded-md px-3 py-2 pr-8 border"
                  >
                    {att.type === 'image' && att.preview ? (
                      <img src={att.preview} alt={att.name} className="w-8 h-8 object-cover rounded-sm" />
                    ) : (
                      <span className="text-lg">{followupFileIcons[att.type] || '\uD83D\uDCCE'}</span>
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate max-w-[100px]">{att.name}</p>
                      <p className="text-[10px] text-muted-foreground">{formatFileSize(att.size)}</p>
                    </div>
                    <button
                      type="button"
                      data-testid={`followup-remove-attachment-${att.id}`}
                      onClick={() => onRemoveFollowupAttachment(att.id)}
                      className="absolute top-1 right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-end">
              <input
                type="file"
                ref={followupFileInputRef}
                onChange={onFollowupFileUpload}
                accept={followupAcceptString}
                multiple
                className="hidden"
                data-testid="followup-file-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => followupFileInputRef.current?.click()}
                disabled={followupLoading || !canAttach}
                className="h-[44px] px-2 flex-shrink-0"
                data-testid="followup-attach-btn"
              >
                <ImagePlus className="w-4 h-4" />
              </Button>
              <Textarea
                data-testid="followup-input"
                placeholder={t('query.followupPlaceholder')}
                value={followupInput}
                onChange={(e) => setFollowupInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (canSend) onSubmit();
                  }
                }}
                className="min-h-[44px] max-h-[120px] text-sm resize-none flex-1"
                disabled={followupLoading}
                rows={1}
              />
              <Button
                data-testid="followup-send-btn"
                size="sm"
                onClick={onSubmit}
                disabled={followupLoading || !canSend}
                className="h-[44px] px-3 flex-shrink-0"
              >
                {followupLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FollowupChat;
